import React, { useMemo, useCallback, useState, useRef } from "react";
import { createStore } from "redux";
import { combineForms, actions, Form, getField } from "react-redux-form";
import { get } from "lodash";
import shortid from "shortid";

const createComponents = (ref) => () => {
  return {
    Form: ({ onChange: baseOnChange, ...props }) => {
      const { forceUpdate, key, store, model } = ref.current;

      const onChange = useCallback(
        (value) => {
          forceUpdate();
          if (baseOnChange) baseOnChange(value);
        },
        [baseOnChange]
      );

      return (
        <Form
          key={key}
          store={store}
          onChange={onChange}
          model={model}
          {...props}
        />
      );
    },
  };
};

const useForm = ({ model = "local", enableStoreLogging = false } = {}) => {
  const [loaded, setLoaded] = useState(false);
  const [, _forceUpdate] = useState();
  const forceUpdate = () => _forceUpdate((v) => !v);
  const valueRef = useRef();
  const fieldRef = useRef();
  const [store, dispatch, key] = useMemo(() => {
    const store = createStore(
      combineForms({
        [model]: {},
      })
    );

    const oldDispatch = store.dispatch;

    const dispatch = (action) => {
      return typeof action === "function"
        ? action(oldDispatch, store.getState)
        : oldDispatch(action);
    };

    store.dispatch = dispatch;

    return [store, dispatch, shortid()];
  }, [model]);

  const state = store.getState();
  fieldRef.current = getField(state, model);
  valueRef.current = get(state, model);

  const setValue = (value) => {
    dispatch(actions.change(model, value, { silent: true }));
    dispatch(actions.setInitial(model));
    setLoaded(true);
    forceUpdate();
  };

  const save = (client, url, item) => {
    return item.id
      ? client.put(`${url}/${item.id}`, item)
      : client.post(url, item);
  };
  const savePatch = (client, url, item) => {
    return item.id
      ? client.patch(`${url}/${item.id}`, item)
      : client.post(url, item);
  };

  const load = async (client, url, id, defaultValue) => {
    return id === "new" ? defaultValue : client.get(`${url}/${id}`).get("data");
  };

  const getTitle = (id, title) => {
    return id === "new" ? `Create ${title}` : `Edit ${title}`;
  };

  const componentRef = useRef();
  componentRef.current = { forceUpdate, key, store, model };

  const components = useMemo(createComponents(componentRef), []);

  return {
    model,
    setValue,
    loaded,
    field: fieldRef.current,
    value: valueRef.current,
    store,
    dispatch,
    save,
    savePatch,
    load,
    valueRef,
    fieldRef,
    actions,
    getTitle,
    ...components,
  };
};

export default useForm;
